import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  IconButton,
  Chip,
  Paper,
  Grid
} from '@mui/material';
import { ArrowBack, FilePresent } from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { handlePreviewFile } from 'src/services/common';

// Download detail interface based on the image structure
interface DownloadDetail {
  vendorName: string;
  vendorEmail: string;
  business: string;
  phone: string;
  downloadedDocument: string;
  documentCaption: string;
  downloadDateTime: string;
  documentUrl?: string;
  vendorBusiness?: string; // Added property for vendorBusiness
  vendorPhone?: string; // Added property for vendorPhone
  documentName?: string; // Added property for documentName
  downloadDate?: string; // Added property for downloadDate
}

// Bid details interface for the header information
interface BidDetails {
  contractNo: string;
  title: string;
  categories: string;
  poNumber: string;
  adDate: string;
  openingDate: string;
  status: string;
  type: string;
}

interface DownloadDetailsProps {
  bidId: string;
  bidDetails: BidDetails;
  onBack: () => void;
  parentBidId: string;
}

const ShowChildDownloads: React.FC<DownloadDetailsProps> = ({
  bidId,
  bidDetails,
  onBack,
  parentBidId
}) => {
  const theme = useTheme();
  const [downloads, setDownloads] = useState<DownloadDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Fetch download details from API
  const fetchDownloadDetails = async (pageNum: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1).toString(),
        Size: pageSize.toString()
      });

      // Replace with your actual API endpoint for download details
      const response = await httpRequest(
        'GET',
        `/bid_portal//bids/${parentBidId}/downloaded_details?${queryParams.toString()}&ChildBidID=${bidId}`
      );

      if (response.status === 'success') {
        // Transform the response data to match our interface
        const transformedDownloads: DownloadDetail[] =
          response.data.Data?.map((item: any) => ({
            vendorName: item.VendorName || item.vendorName || '',
            vendorEmail: item.VendorEmail || item.vendorEmail || '',
            business: item.Business || item.business || '',
            phone: item.Phone || item.phone || '',
            downloadedDocument:
              item.DownloadedDocument || item.downloadedDocument || '',
            documentCaption: item.DocumentCaption || item.documentCaption || '',
            downloadDateTime: item.DownloadDate || item.downloadDate || '',
            documentUrl: item.DocumentUrl || item.documentUrl || '',
            vendorBusiness: item.VendorBusiness || item.vendorBusiness || '', // Ensure correct field name here
            vendorPhone: item.VendorPhone || item.vendorPhone || '', // Ensure correct field name here
            documentName: item.DocumentName || item.documentName || '', // Ensure correct field name here
            downloadDate: item.DownloadDate || item.downloadDate || '' // Ensure correct field name here
          })) || [];

        setDownloads(transformedDownloads);
        setTotalCount(
          response.data.TotalRecords || transformedDownloads.length
        );
      } else {
        setError(response.message || 'Failed to fetch download details');
        toast.error(response.message || 'Failed to fetch download details');
      }
    } catch (err) {
      console.error('Error fetching download details:', err);
      setError('Failed to fetch download details. Please try again later.');
      toast.error('Failed to fetch download details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDownloadDetails(page, rowsPerPage);
  }, [bidId, page, rowsPerPage]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Return to bid list function
  const returnToBidList = () => {
    // setViewMode('list');
    // setSelectedBid(null);
    onBack();
  };

  const formatDownloadDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return '-';
    }
  };
  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          gap: 2
        }}
      >
        <IconButton
          onClick={returnToBidList}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 190,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Return to My Downloads
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Title:
              </Typography>
              <Typography variant="body1">{bidDetails.title}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Categories:
              </Typography>
              <Typography variant="body1">{bidDetails?.categories}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Type:
              </Typography>
              <Typography variant="body1">{bidDetails.type}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Ad Date:
              </Typography>
              <Typography variant="body1">
                {bidDetails.adDate
                  ? new Date(bidDetails.adDate).toLocaleDateString('en-US')
                  : ''}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Opening Date:
              </Typography>
              <Typography variant="body1">
                {bidDetails.openingDate
                  ? formatDownloadDate(bidDetails.openingDate)
                  : ''}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Status:
              </Typography>
              <Chip
                label={bidDetails.status}
                color={
                  bidDetails.status.toLowerCase() === 'open'
                    ? 'success'
                    : bidDetails.status.toLowerCase() === 'closed'
                    ? 'warning'
                    : bidDetails.status.toLowerCase() === 'awarded'
                    ? 'info'
                    : 'default'
                }
                size="small"
              />
            </Box>
          </Grid>

          {bidDetails.poNumber && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{ fontWeight: 'bold', mr: 1 }}
                >
                  PO Number:
                </Typography>
                <Typography variant="body1">{bidDetails.poNumber}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
        >
          {error}
        </Alert>
      )}

      <Box>
        <Typography variant="h6" gutterBottom>
          Child Bid Downloaded Documents
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table aria-label="child downloaded documents table">
              <TableHead>
                <TableRow>
                  <TableCell>Document Name</TableCell>
                  <TableCell>Download Date & Time</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {downloads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                        <FilePresent
                          sx={{
                            fontSize: 40,
                            color: alpha(theme.palette.text.secondary, 0.6)
                          }}
                        />
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          No download records found for this bid.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  downloads.map((doc, index) => (
                    <TableRow key={index}>
                      <TableCell>{doc.documentName}</TableCell>
                      <TableCell>
                        {formatDownloadDate(doc.downloadDateTime)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            handlePreviewFile(doc.downloadedDocument)
                          }
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ShowChildDownloads;
